import {type TranslationObject} from '@colonist/i18n'
import {type LobbyImage, type TimeoutId} from '@colonist/types'
import {ArrayUtils} from '@colonist/utils'
import {UICornerNotificationView} from './UICornerNotificationView.ts'
import {NotificationContentType} from '../../../shared/scripts/ui_utils/NotificationUtils.ts'

interface CornerNotificationRecord {
    notificationView: UICornerNotificationView
    timeout?: TimeoutId
}

export class UICornerNotificationController {
    private static readonly notifications: CornerNotificationRecord[] = []
    static readonly DURATION: number = 5000
    private static readonly MAX_NON_PERMANENT_NOTIFICATIONS = 3

    static showNotification(icon: LobbyImage, text: TranslationObject, duration?: number, action?: () => void, removeAction?: () => void, isPermanent?: boolean, contentType: NotificationContentType = NotificationContentType.General): UICornerNotificationView {
        duration = duration ?? this.DURATION

        if(this.shouldEditPreviousNotification()) return this.editPreviousNotification(duration, text, isPermanent)
        return this.createNotification(icon, text, duration, action, removeAction, isPermanent, contentType)
    }

    private static shouldEditPreviousNotification() {
        if(this.notifications.length == 0) return false
        const notificationTypesToEdit = [NotificationContentType.Volume]
        const lastNotification = this.notifications[this.notifications.length - 1]
        return notificationTypesToEdit.includes(lastNotification.notificationView.contentType)
    }

    private static editPreviousNotification(duration: number, text: TranslationObject, isPermanent?: boolean) {
        const lastNotification = this.notifications[this.notifications.length - 1]
        clearTimeout(lastNotification.timeout)
        lastNotification.notificationView.resetTimerAnimation(duration)
        lastNotification.notificationView.updateText(text)
        if(!isPermanent) this.setNotificationTimeout(lastNotification, duration)
        return lastNotification.notificationView
    }

    private static createNotification(icon: LobbyImage, text: TranslationObject, duration: number, action?: () => void, removeAction?: () => void, isPermanent?: boolean, contentType: NotificationContentType = NotificationContentType.General): UICornerNotificationView {
        const newNotificationView = new UICornerNotificationView(icon, text, duration, action, removeAction, isPermanent, contentType)
        const newNotification: CornerNotificationRecord = {notificationView: newNotificationView}
        this.notifications.push(newNotification)
        const newRemoveAction = () => {
            if(removeAction != undefined) removeAction()
            this.removeNotification(newNotification)
        }
        newNotificationView.updateRemoveAction(newRemoveAction)
        this.removeExtraNotification()
        if(!isPermanent) this.setNotificationTimeout(newNotification, duration)
        return newNotificationView
    }

    private static setNotificationTimeout(notification: CornerNotificationRecord, duration: number) {
        notification.timeout = setTimeout(() => {
            this.removeNotification(notification)
        }, duration)
    }

    private static removeNotification(notification: CornerNotificationRecord) {
        if(!ArrayUtils.removeElementFromArray(this.notifications, notification)) return
        notification.notificationView.close()
        clearTimeout(notification.timeout)
    }

    private static removeExtraNotification() {
        const nonPermanentNotifications = this.notifications.filter((notification) => !notification.notificationView.isPermanent)
        if(nonPermanentNotifications.length <= this.MAX_NON_PERMANENT_NOTIFICATIONS) return
        this.removeNotification(nonPermanentNotifications[0])
    }
}
